import { forwardRef } from "react";
import { DisclosureArrow } from "../../disclosure";
import { HiddenSelect } from "./HiddenSelect";
import { Listbox } from "../../listbox";
import { augmentElement, cssModule, isNil, mergeProps } from "../../shared";
import { Overlay } from "../../overlay";
import { useResponsiveValue } from "../../styling";
import { Span } from "../../html";
import { Text } from "../../typography";
import { useFieldInputProps } from "../../field";
import { useInputGroupSelectAddonProps } from "../../input-group";
import { useSelect } from "./useSelect";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const DefaultElement = "button";
export function InnerSelect(props) {
  const [fieldProps] = useFieldInputProps();
  const [inputGroupProps] = useInputGroupSelectAddonProps();
  const {
    active,
    align = "start",
    allowFlip = true,
    allowPreventOverflow = true,
    allowResponsiveMenuWidth,
    "aria-describedby": ariaDescribedBy,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    as: TriggerType = DefaultElement,
    autoFocus,
    children,
    defaultOpen,
    defaultSelectedKey,
    direction = "bottom",
    disabled,
    fluid,
    focus,
    forwardedRef,
    hover,
    icon,
    id,
    name,
    onOpenChange,
    onSelectionChange,
    open,
    overlayProps: overlayPropsProp,
    placeholder,
    // Usually provided by the field inputs.
    readOnly,
    required,
    selectedKey: selectedKeyProp,
    validationState,
    variant = "outline",
    zIndex = 10000,
    ...rest
  } = mergeProps(props, fieldProps, inputGroupProps);
  const alignValue = useResponsiveValue(align);
  const directionValue = useResponsiveValue(direction);
  const {
    isOpen,
    listboxProps,
    overlayProps,
    selectedItem,
    selectedKey,
    triggerProps
  } = useSelect(children, {
    align: alignValue,
    allowFlip,
    allowPreventOverflow,
    allowResponsiveMenuWidth: allowResponsiveMenuWidth !== null && allowResponsiveMenuWidth !== void 0 ? allowResponsiveMenuWidth : variant !== "ghost",
    ariaDescribedBy,
    ariaLabel,
    ariaLabelledBy,
    autoFocus,
    defaultOpen,
    defaultSelectedKey,
    direction: directionValue,
    disabled,
    id,
    onOpenChange,
    onSelectionChange,
    open,
    overlayProps: overlayPropsProp,
    readOnly,
    ref: forwardedRef,
    selectedKey: selectedKeyProp,
    validationState
  });
  const iconMarkup = icon && augmentElement(icon, {
    className: "o-ui-select-icon",
    size: "sm"
  });
  const selectedIconMarkup = (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.icon) && augmentElement(selectedItem.icon, {
    className: "o-ui-select-value-start-icon",
    size: "sm"
  });

  const selectedTextMarkup = (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.text) && /*#__PURE__*/_jsx(Text, {
    className: "o-ui-select-value-text",
    children: selectedItem.text
  });

  const selectedEndIconMarkup = (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.endIcon) && augmentElement(selectedItem.endIcon, {
    className: "o-ui-select-value-end-icon",
    size: "sm"
  });
  const valueMarkup = isNil(selectedItem) ? placeholder && /*#__PURE__*/_jsx(Text, {
    className: "o-ui-select-placeholder",
    children: placeholder
  }) : /*#__PURE__*/_jsxs(Span, {
    className: "o-ui-select-value",
    children: [selectedIconMarkup, selectedTextMarkup, selectedEndIconMarkup]
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(HiddenSelect, {
      disabled: disabled,
      name: name,
      required: required,
      selectedKey: selectedKey,
      validationState: validationState
    }), /*#__PURE__*/_jsxs(TriggerType, { ...mergeProps(rest, {
        className: cssModule("o-ui-select-trigger", variant, validationState, fluid && "fluid", active && "active", focus && "focus", hover && "hover", isNil(selectedItem) && "has-placeholder")
      }, triggerProps),
      children: [iconMarkup, valueMarkup, /*#__PURE__*/_jsx(DisclosureArrow, {
        open: isOpen,
        size: "sm"
      })]
    }), /*#__PURE__*/_jsx(Overlay, { ...overlayProps,
      zIndex: zIndex,
      children: /*#__PURE__*/_jsx(Listbox, { ...listboxProps
      })
    })]
  });
}
InnerSelect.defaultElement = DefaultElement;
export const Select = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerSelect, { ...props,
  forwardedRef: ref
}));